import {
  ArrowRight,
  BookOpenText,
  Building2,
  Copy,
  FileBadge,
  Files,
  LibraryBig,
  LockKeyhole,
  MessageSquareText,
  Presentation,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CopyButton } from "../components/CopyButton";
import { GlobalSearch } from "../components/GlobalSearch";
import { ProjectOverviewCard } from "../components/ProjectOverviewCard";
import { SearchResults } from "../components/SearchResults";
import { projectPassport } from "../data/projectPassport";
import { formatDate } from "../lib/dates";
import { searchAll } from "../lib/search";
import { getTelegramUser } from "../lib/telegram";

export function HomePage() {
  const [query, setQuery] = useState("");
  const results = useMemo(() => searchAll(query), [query]);
  const user = getTelegramUser();

  return (
    <div className="page home-page">
      <section className="home-hero">
        <div className="home-hero__brand">
          <img src="/assets/social-logo-white.svg" alt="Social" />
          <span>by AKFA</span>
        </div>
        <div className="home-hero__status">
          <LockKeyhole size={14} aria-hidden="true" />
          Внутренний ресурс
        </div>
        <div className="home-hero__content">
          <span className="home-hero__welcome">{user?.first_name ? `Здравствуйте, ${user.first_name}` : "Добро пожаловать."}</span>
          <h1>Social<br />Knowledge Hub</h1>
          <p>Актуальная информация и материалы проекта</p>
          <div className="home-hero__updated">
            <span />
            База обновлена {formatDate(projectPassport.lastUpdatedAt)}
          </div>
        </div>
        <div className="home-hero__shape home-hero__shape--one" />
        <div className="home-hero__shape home-hero__shape--two" />
      </section>

      <section className="home-search" aria-label="Глобальный поиск">
        <div className="section-kicker">Быстрый поиск</div>
        <GlobalSearch onQueryChange={setQuery} />
        <SearchResults query={query} results={results} compact />
      </section>

      <section className="home-section">
        <div className="section-heading section-heading--row">
          <div>
            <span className="eyebrow">База знаний</span>
            <h2>Всё важное — в четырёх разделах</h2>
          </div>
          <span className="section-index">01—04</span>
        </div>
        <div className="overview-grid">
          <ProjectOverviewCard
            to="/passport"
            title="Паспорт проекта"
            description="Миссия, позиционирование, аудитория и структура проекта."
            icon={BookOpenText}
            tone="red"
            meta="01 · Основа"
          />
          <ProjectOverviewCard
            to="/facts"
            title="Подтверждённые факты"
            description="Актуальные цифры, формулировки, источники и даты проверки."
            icon={LibraryBig}
            tone="blue"
            meta="02 · 12 фактов"
          />
          <ProjectOverviewCard
            to="/wording"
            title="Как говорить о проекте"
            description="Готовые описания для разных форматов и аудиторий."
            icon={MessageSquareText}
            tone="green"
            meta="03 · 7 текстов"
          />
          <ProjectOverviewCard
            to="/assets"
            title="Бренд-материалы"
            description="Логотипы, брендбук, презентации, фотографии и документы."
            icon={Files}
            tone="lavender"
            meta="04 · 10 материалов"
          />
        </div>
      </section>

      <section className="home-section">
        <div className="section-heading section-heading--row">
          <div>
            <span className="eyebrow">Быстрый доступ</span>
            <h2>Частые действия</h2>
          </div>
        </div>
        <div className="quick-grid">
          <div className="quick-action quick-action--copy">
            <span className="quick-action__icon"><Copy size={20} aria-hidden="true" /></span>
            <div><strong>Краткое описание</strong><span>Готово для копирования</span></div>
            <CopyButton text={projectPassport.shortDescription} label="Копировать" />
          </div>
          <Link className="quick-action" to="/facts">
            <span className="quick-action__icon"><Building2 size={20} aria-hidden="true" /></span>
            <div><strong>Ключевые цифры</strong><span>12 фактов с источниками</span></div>
            <ArrowRight size={19} aria-hidden="true" />
          </Link>
          <Link className="quick-action" to="/assets/main-logo">
            <span className="quick-action__icon"><FileBadge size={20} aria-hidden="true" /></span>
            <div><strong>Актуальный логотип</strong><span>Официальный SVG</span></div>
            <ArrowRight size={19} aria-hidden="true" />
          </Link>
          <Link className="quick-action" to="/assets/social-ppt-en">
            <span className="quick-action__icon"><Presentation size={20} aria-hidden="true" /></span>
            <div><strong>Презентация проекта</strong><span>Project Overview · EN</span></div>
            <ArrowRight size={19} aria-hidden="true" />
          </Link>
        </div>
      </section>

      <aside className="internal-note">
        <LockKeyhole size={21} aria-hidden="true" />
        <div>
          <strong>Перед публикацией</strong>
          <p>Всегда проверяйте статус, уровень публичности и источник. Числа со статусом «Требует проверки» нельзя автоматически переносить в официальные материалы.</p>
        </div>
      </aside>
    </div>
  );
}
